import { slicedToArray as _slicedToArray } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState } from 'react';
import PropTypes from 'prop-types';
import { Root, Slider } from './Slider.styles.js';
import { FadeIn } from '../../../Animations/FadeIn/FadeIn.js';
import { OPEN_ENDS, MAX, MIN, STEP, NO_OP } from '../constants.js';
import useSliderScaleFactor from './useSliderScaleFactor.js';
import ScreenReaderOnly from '../../../ScreenReaderOnly/index.js';
import Rail from './Rail.js';
import RangeSelection from './RangeSelection.js';

var bySmallestFirst = function bySmallestFirst(a, b) {
  return a - b;
};
var HANDLES = {
  MAX: 'max',
  MIN: 'min'
};
var SliderMultiple = function SliderMultiple(_ref) {
  var _ref$id = _ref.id,
    id = _ref$id === void 0 ? undefined : _ref$id,
    _ref$isDisabled = _ref.isDisabled,
    isDisabled = _ref$isDisabled === void 0 ? false : _ref$isDisabled,
    _ref$isPushable = _ref.isPushable,
    isPushable = _ref$isPushable === void 0 ? false : _ref$isPushable,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? MAX : _ref$max,
    _ref$min = _ref.min,
    min = _ref$min === void 0 ? MIN : _ref$min,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? NO_OP : _ref$onChange,
    _ref$onChangeComplete = _ref.onChangeComplete,
    onChangeComplete = _ref$onChangeComplete === void 0 ? NO_OP : _ref$onChangeComplete,
    _ref$openEnds = _ref.openEnds,
    openEnds = _ref$openEnds === void 0 ? undefined : _ref$openEnds,
    _ref$step = _ref.step,
    step = _ref$step === void 0 ? STEP : _ref$step,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? undefined : _ref$testId,
    value = _ref.value;
  var _useSliderScaleFactor = useSliderScaleFactor({
      max: max,
      min: min
    }),
    _useSliderScaleFactor2 = _slicedToArray(_useSliderScaleFactor, 2),
    ref = _useSliderScaleFactor2[0],
    scaleFactor = _useSliderScaleFactor2[1];
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    activeRangeIndex = _useState2[0],
    setActiveRangeIndex = _useState2[1];
  var _value = _slicedToArray(value, 2),
    _value$ = _value[0],
    minValue = _value$ === void 0 ? min : _value$,
    _value$2 = _value[1],
    maxValue = _value$2 === void 0 ? max : _value$2;
  var createOnChangeHandler = function createOnChangeHandler() {
    var handle = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : HANDLES.MIN;
    var callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : NO_OP;
    return function (e) {
      var newValue = +e.currentTarget.value;
      var newValues = handle === HANDLES.MIN ? [newValue, maxValue] : [minValue, newValue];
      if (isPushable) {
        if (handle === HANDLES.MIN && newValue >= maxValue) {
          var newMaxValue = Math.min(max, newValue + step);
          if (newMaxValue - maxValue >= step) {
            newValues = [maxValue, newMaxValue];
          }
        } else if (handle === HANDLES.MAX && newValue <= minValue) {
          var newMinValue = Math.max(min, newValue - step);
          if (minValue - newMinValue >= step) {
            newValues = [newMinValue, minValue];
          }
        }
      }
      return callback(e, newValues.sort(bySmallestFirst));
    };
  };
  var handleMinChange = createOnChangeHandler(HANDLES.MIN, onChange);
  var handleMaxChange = createOnChangeHandler(HANDLES.MAX, onChange);
  var handleMinChangeComplete = createOnChangeHandler(HANDLES.MIN, onChangeComplete);
  var handleMaxChangeComplete = createOnChangeHandler(HANDLES.MAX, onChangeComplete);
  var handleMouseMove = function handleMouseMove(e) {
    var position = e.nativeEvent.offsetX / scaleFactor + min;
    var closestIndex = Math.abs(minValue - position) < Math.abs(maxValue - position) ? 0 : 1;
    setActiveRangeIndex(closestIndex);
  };
  return React__default.createElement(Root, {
    ref: ref,
    $disabled: isDisabled,
    "data-testid": testId,
    id: id,
    onMouseMove: handleMouseMove
  }, React__default.createElement(FadeIn, null, React__default.createElement(Rail, {
    openEnds: openEnds
  }), React__default.createElement(RangeSelection, {
    max: max,
    min: min,
    openEnds: openEnds,
    scaleFactor: scaleFactor,
    value: [minValue, maxValue]
  }), React__default.createElement(ScreenReaderOnly, {
    as: "label",
    htmlFor: id && "".concat(id, "-min")
  }, minValue), React__default.createElement(Slider, {
    disabled: isDisabled,
    "data-active": activeRangeIndex === 0,
    id: id && "".concat(id, "-min"),
    max: max,
    min: min,
    onBlur: handleMinChangeComplete,
    onChange: handleMinChange,
    onKeyUp: handleMinChangeComplete,
    onMouseUp: handleMinChangeComplete,
    onTouchEnd: handleMinChangeComplete,
    step: step,
    type: "range",
    value: minValue
  }), React__default.createElement(ScreenReaderOnly, {
    as: "label",
    htmlFor: id && "".concat(id, "-max")
  }, maxValue), React__default.createElement(Slider, {
    disabled: isDisabled,
    "data-active": activeRangeIndex === 1,
    id: id && "".concat(id, "-max"),
    max: max,
    min: min,
    onBlur: handleMaxChangeComplete,
    onChange: handleMaxChange,
    onKeyUp: handleMaxChangeComplete,
    onMouseUp: handleMaxChangeComplete,
    onTouchEnd: handleMaxChangeComplete,
    step: step,
    type: "range",
    value: maxValue
  })));
};
SliderMultiple.propTypes = {
  id: PropTypes.string,
  isDisabled: PropTypes.bool,
  isPushable: PropTypes.bool,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func,
  onChangeComplete: PropTypes.func,
  openEnds: PropTypes.oneOf(Object.values(OPEN_ENDS)),
  step: PropTypes.number,
  testId: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.number).isRequired
};
var SliderMultiple$1 = SliderMultiple;

export { SliderMultiple$1 as default };
//# sourceMappingURL=SliderMultiple.js.map
